import React from "react";
import { useReveal } from "@/lib/useReveal";
import { SocialLogo, SUPPORTED_PLATFORMS } from "@/components/landing/SocialLogos";

/**
 * Transparent "sources we scan" section. Uses actual brand logos so visitors
 * see exactly where signals come from - and understand we only touch public
 * content, not private DMs or closed groups.
 */
export default function SupportedPlatforms() {
  const [head, headVisible] = useReveal();

  return (
    <section id="sources" className="relative lm-cv py-16 md:py-20 bg-lm-bg overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div
          ref={head}
          className={`max-w-3xl mb-10 lm-reveal ${headVisible ? "is-visible" : ""}`}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
            Where signals come from
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-semibold leading-tight tracking-tight text-lm-text">
            Every public conversation. One place.
          </h2>
          <p className="mt-4 text-base md:text-lg text-lm-text2 leading-relaxed">
            Leadmato reads across the platforms your buyers actually hang out on —
            public content only, ranked by intent and opportunity.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          {SUPPORTED_PLATFORMS.map((p, i) => (
            <PlatformCard key={p.id} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PlatformCard({ p, index }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`flex flex-col items-center text-center gap-3 lm-reveal ${
        visible ? "is-visible" : ""
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
      data-testid={`platform-card-${p.id}`}
    >
      <SocialLogo platform={p.id} className="w-14 h-14 md:w-16 md:h-16 shrink-0 transition-transform hover:-translate-y-1 hover:scale-105" />
      <div>
        <div className="font-display font-semibold text-lm-text">{p.name}</div>
        <div className="text-xs text-lm-text2 leading-snug mt-0.5">{p.subtitle}</div>
      </div>
    </div>
  );
}
